import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createFeeGroup, updateFeeGroup, deleteFeeGroup } from "redux/admin/crypto-payout-fee-groups/cryptoPayoutFeeGroupsSlice";
import { MdEdit, MdDelete, MdClose, MdAdd } from "react-icons/md";
import swal from 'sweetalert';

const CryptoPayoutFeeGroups = ({ data, resellers }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    rate_offset: "",
    reseller_ids: []
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      setFormData({ name: "", rate_offset: "", reseller_ids: [] });
      setEditingGroup(null);
      setSearchTerm("");
    }
  }, [showModal]);

  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({ name: "", rate_offset: "", reseller_ids: [] });
    setShowModal(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      rate_offset: group.rate_offset,
      reseller_ids: group.reseller_ids || []
    });
    setShowModal(true);
  };

  const handleDelete = (id, name) => {
    swal({
      title: "Emin misiniz?",
      text: `"${name}" grubunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      icon: "warning",
      buttons: {
        cancel: "İptal",
        confirm: {
          text: "Sil",
          value: true,
        }
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        dispatch(deleteFeeGroup(id));
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || formData.name.trim().length < 3) {
      swal("Hata", "Grup adı en az 3 karakter olmalıdır!", "error");
      return;
    }

    if (formData.rate_offset === "" || isNaN(parseFloat(formData.rate_offset))) {
      swal("Hata", "Geçerli bir oran girmelisiniz!", "error");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      rate_offset: parseFloat(formData.rate_offset),
      reseller_ids: formData.reseller_ids
    };

    if (editingGroup) {
      // Update - include ID
      dispatch(updateFeeGroup({ id: editingGroup.id, ...payload }));
    } else {
      // Create
      dispatch(createFeeGroup(payload));
    }
    
    setShowModal(false);
  };

  const addReseller = (resellerId) => {
    if (!formData.reseller_ids.includes(resellerId)) {
      setFormData({
        ...formData,
        reseller_ids: [...formData.reseller_ids, resellerId]
      });
    }
    setSearchTerm("");
  };

  const removeReseller = (resellerId) => {
    setFormData({
      ...formData,
      reseller_ids: formData.reseller_ids.filter(id => id !== resellerId)
    });
  };

  // Get available resellers (not already added)
  const availableResellers = resellers?.filter(
    r => !formData.reseller_ids.includes(r.id)
  ) || [];

  // Filter resellers based on search term
  const filteredResellers = availableResellers.filter(r => {
    const searchLower = searchTerm.toLowerCase();
    return (
      r.name?.toLowerCase().includes(searchLower) ||
      r.phone?.toLowerCase().includes(searchLower) ||
      r.payment_key?.toLowerCase().includes(searchLower)
    );
  });

  // Get selected resellers details
  const selectedResellers = formData.reseller_ids.map(id => 
    resellers?.find(r => r.id === id)
  ).filter(Boolean);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-white-100">Kripto Ödeme Komisyon Grupları</h3>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded-md font-semibold flex items-center gap-2"
        >
          <MdAdd size={20} /> Yeni Grup Oluştur
        </button>
      </div>

      {/* Fee Groups Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y-2 divide-gray-700 text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-white-100">Grup Adı</th>
              <th className="px-4 py-2 text-left text-white-100">USDTRY Kur</th>
              <th className="px-4 py-2 text-left text-white-100">Üye Sayısı</th>
              <th className="px-4 py-2 text-left text-white-100">Üyeler</th>
              <th className="px-4 py-2 text-left text-white-100">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {data?.map((group) => (
              <tr key={group.id} className="hover:bg-gray-800/30">
                <td className="px-4 py-2 text-white-100 font-medium">{group.name}</td>
                <td className="px-4 py-2 text-white-100">{group.rate_offset}</td>
                <td className="px-4 py-2 text-white-100">{group.members?.length || 0}</td>
                <td className="px-4 py-2 text-white-100">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {group.members?.slice(0, 3).map((member) => (
                      <span 
                        key={member.id}
                        className="bg-gray-700 px-2 py-1 rounded text-xs"
                      >
                        {member.name}
                      </span>
                    ))}
                    {group.members?.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{group.members.length - 3} daha
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-white-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(group)}
                      className="bg-blue-600 text-white rounded-md px-3 py-1 flex items-center gap-1 font-semibold hover:bg-blue-700"
                    >
                      <MdEdit /> Düzenle
                    </button>
                    <button
                      onClick={() => handleDelete(group.id, group.name)}
                      className="bg-red-500 text-white rounded-md px-3 py-1 flex items-center gap-1 font-semibold hover:bg-red-600"
                    >
                      <MdDelete /> Sil
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-semibold text-white-100">
                {editingGroup ? "Grubu Düzenle" : "Yeni Grup Oluştur"}
              </h4>
              <button
                onClick={() => setShowModal(false)}
                className="text-white-100 hover:text-red-500"
              >
                <MdClose size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Group Name */}
              <div className="mb-4">
                <label className="block text-white-100 font-medium mb-2">
                  Grup Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 rounded-md bg-gray-700 text-white-100 border border-gray-600"
                  placeholder="VIP Bayiler"
                  required
                  minLength={3}
                />
              </div>

              {/* Rate Offset */}
              <div className="mb-4">
                <label className="block text-white-100 font-medium mb-2">
                  USDTRY kur <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.rate_offset}
                  onChange={(e) => setFormData({ ...formData, rate_offset: e.target.value })}
                  className="w-full p-2 rounded-md bg-gray-700 text-white-100 border border-gray-600"
                  placeholder="42.00"
                  required
                />
              </div>

              {/* Current Members */}
              {selectedResellers.length > 0 && (
                <div className="mb-4">
                  <label className="block text-white-100 font-medium mb-2">
                    Mevcut Üyeler
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedResellers.map((reseller) => (
                      <div
                        key={reseller.id}
                        className="bg-primary text-white px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span className="text-sm">
                          {reseller.name} ({reseller.phone})
                        </span>
                        <button
                          type="button"
                          onClick={() => removeReseller(reseller.id)}
                          className="hover:text-red-300"
                        >
                          <MdClose size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Resellers */}
              <div className="mb-4">
                <label className="block text-white-100 font-medium mb-2">
                  Bayi Ekle
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 rounded-md bg-gray-700 text-white-100 border border-gray-600"
                  placeholder="İsim, telefon veya payment key ile ara..."
                />
                
                {searchTerm && (
                  <div className="mt-2 max-h-48 overflow-y-auto bg-gray-700 rounded-md border border-gray-600">
                    {filteredResellers.length > 0 ? (
                      filteredResellers.map((reseller) => (
                        <button
                          key={reseller.id}
                          type="button"
                          onClick={() => addReseller(reseller.id)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-600 text-white-100 border-b border-gray-600 last:border-b-0"
                        >
                          {reseller.name} ({reseller.phone})
                          {reseller.payment_key && (
                            <span className="text-xs text-gray-400 ml-2">
                              [{reseller.payment_key}]
                            </span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-400 text-sm">
                        Sonuç bulunamadı
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-700"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-blue-700"
                >
                  {editingGroup ? "Güncelle" : "Oluştur"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoPayoutFeeGroups;